import useWebsocket from "@/hooks/useWebsocket";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { DistributedRewardResponse, NotificationType, PrizePoolNotificationMetadata, RewardsApi, RewardType, WebsocketEvent } from "@devour/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { GuildPrizeWonDialogFromDistributedReward } from "@/pages/guilds/GuildPrizeWonDialog";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import { isDesktop, isTablet } from "react-device-detect";
import Toast from "./Toast";
import { BrandChallengePrizeWonModalStateless } from "@/pages/challenge/BrandChallengePrizeWonModal";
import PromoTokenWonModal from "@/components/modals/promoToken/PromoTokenWonModal";
import {useSearchParams} from "react-router";
import {useGetDistributedReward} from "@/hooks/useGetDistributedReward";

const useGetDistributedRewardsOwn = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const client = new RewardsApi(getConfig(fullToken));
    return useQuery({
        queryFn: async () => {
            return client.getDistributedRewardsOwn({
                webNotificationDismissed: false,
            });
        },
        queryKey: ["getDistributedRewardsOwn"],
        enabled: Boolean(fullToken),
    });
};

const useDismissDistributedReward = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const client = new RewardsApi(getConfig(fullToken));
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return client.dismissNotificationWeb({
                id: id,
            });
        },
        mutationKey: ["dismissDistributedReward"],
        onSuccess: () => {
            // should we invalidate the query here?
            queryClient.invalidateQueries({ queryKey: ["getDistributedRewardsOwn"] });
        },
    });
};

const getClaimedRewardsString = (rewards: Array<{ rewardType: RewardType; amount: number }>) => {
    const formattedRewards = rewards.map((reward) => `${reward.amount} ${reward.rewardType}`);
    if (formattedRewards.length === 0) {
        return "";
    }

    if (formattedRewards.length === 1) {
        // Single item, return as is
        return `${formattedRewards[0]} successfully claimed!`;
    }

    if (formattedRewards.length === 2) {
        // Two items, join with "and"
        return `${formattedRewards.join(" and ")} successfully claimed!`;
    }

    /*
     * More than two items:
     * Join all but the last two items with commas, then add "and" before the last item.
     */
    const allButLast = formattedRewards.slice(0, -1);
    const last = formattedRewards[formattedRewards.length - 1];
    return `${allButLast.join(", ")}, and ${last} successfully claimed!`;
};

const OverwolfXpFuelToast = ({ rewardType, amount, onDismiss}: { rewardType: RewardType, amount: number, onDismiss: () => void }) => {
    const claimedRewardsString = getClaimedRewardsString([{ rewardType, amount }]);
    const [isOpen, setIsOpen] = useState(true);
    const {isOnOverwolf} = useOverwolfInterop();
    const isOnMobile = !isDesktop && !isTablet;

    const getToastStyle = () => {
        if (isOnMobile && isOnOverwolf) {
            return {
                maxWidth: "100vw",
                width: "100vw",
                borderRadius: "0",
                marginBottom: "0",
                marginLeft: "0",
            };
        }

        return {
            marginBottom: "0",
        };
    };
    return (
        <Toast
            message={claimedRewardsString}
            isOpen={isOpen}
            onDismiss={() => {
                setIsOpen(false);
                onDismiss();
            }}
            removeMarginAdjustment={true}
            className={classNames("overwolf-govip_rewards_claim", {
                "overwolf-govip_rewards_claim_in-game": isOnMobile && isOnOverwolf,
            })}
            forceDarkMode={true}
            variant={"success"}
            style={isOnOverwolf && getToastStyle()}
        />
    );

};


export const GlobalRewardNotifications = ({children}: PropsWithChildren) => {
    const [searchParams] = useSearchParams();
    const distributedRewardId = searchParams.get("distribute_reward");
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: queryParamsDistributedReward} = useGetDistributedReward(distributedRewardId, fullToken);
    const [showModal, setShowModal] = useState<boolean>(true);
    const {data: distributedRewards} = useGetDistributedRewardsOwn();
    const queryClient = useQueryClient();
    useWebsocket(WebsocketEvent.REWARDDISTRIBUTED, () => {
        queryClient.invalidateQueries({ queryKey: ["getDistributedRewardsOwn"] });
    });
    const {
        mutateAsync: dismissReward,
    } = useDismissDistributedReward();

    function onDismiss(rewardId: string) {
        setShowModal(false);
        void dismissReward(rewardId);
    }

    const renderRewardNotification = (reward: DistributedRewardResponse) => {
        switch (reward.notificationMetadata?.notificationType) {
            case NotificationType.SPONSOREDCOMPETITIONWINNER:
                return (
                    <GuildPrizeWonDialogFromDistributedReward
                        isOpen={showModal}
                        onClose={() => onDismiss(reward.id)}
                        onDismiss={() => onDismiss(reward.id)}
                        reward={reward}
                    />
                );
            case NotificationType.PROMOTOKEN:
                return (
                    <PromoTokenWonModal
                        isOpen={showModal}
                        onClose={() => onDismiss(reward.id)}
                        reward={reward}
                    />
                );
            case NotificationType.PRIZEPOOL:
                const prizePoolMetadata = reward.notificationMetadata as PrizePoolNotificationMetadata;
                return (
                    <BrandChallengePrizeWonModalStateless
                        assetId= {prizePoolMetadata.assetId}
                        challengeEndDate={prizePoolMetadata.challengeEndDate}
                        challengeName={prizePoolMetadata.challengeName}
                        rewardName={prizePoolMetadata.rewardName}
                        winnerMessage={prizePoolMetadata.winnerMessage}
                        onDismiss={() => onDismiss(reward.id)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            {distributedRewards?.rewards.map((reward) => renderRewardNotification(reward))}
            {queryParamsDistributedReward && renderRewardNotification(queryParamsDistributedReward)}
            {children}
        </>
    );
};